import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { AppMobileMenu } from './AppMobileMenu';
import { CountryPicker } from './zakat/CountryPicker';
import { DesktopMoreMenu } from './DesktopMoreMenu';
import { SettingsSheet } from './SettingsSheet';
import { HeaderShareButton } from './HeaderShareButton';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * SiteHeader — single-row responsive homepage header (~64px tall).
 *
 * Replaces the previous two-row utility + nav stack on ZakatGlobalPage.
 * A single logo/wordmark markup instance is used across all breakpoints;
 * responsive changes are CSS-driven (Tailwind), never duplicated.
 *
 * Structure (left → right):
 *   • Brand: logo + wordmark (compact ≤md, full xl+)
 *   • Primary nav: Learn · Laws · Women (md+ only)
 *   • Actions: [Share] (md+ only), Settings chip, Hamburger (mobile only)
 *
 * The "EN" settings chip opens `SettingsSheet` (accessible dialog) that
 * consolidates country · language · text-size controls.
 */
export const SiteHeader = ({ onOpenFaq }) => {
  const navigate = useNavigate();
  const {
    t, selectedLanguage, languages, updateLanguage,
  } = useZakatGlobal();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const settingsTriggerRef = React.useRef(null);

  // Restore focus to the settings chip after the sheet closes — Radix
  // Dialog can only do this automatically when its own DialogTrigger is
  // used; here we drive `open` externally so we manage focus ourselves.
  const handleSettingsOpenChange = (next) => {
    setSettingsOpen(next);
    if (!next) {
      // Wait a tick for the dialog to unmount before refocusing.
      setTimeout(() => settingsTriggerRef.current?.focus(), 0);
    }
  };

  return (
    <header
      className="site-header fixed top-0 left-0 right-0 z-50 border-b border-[#E2DCD5]"
      style={{ paddingTop: 'env(safe-area-inset-top)', background: 'rgba(250,247,240,0.97)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="site-header__inner flex items-center justify-between gap-3 h-10 md:h-12 flex-nowrap min-w-0">
          {/* Brand — single instance, responsive via CSS */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="site-header__brand flex items-center gap-2 shrink min-w-0 group cursor-pointer bg-transparent border-0"
            data-testid="site-header-brand"
            aria-label="Amanah by Zakat.International — home"
          >
            <img
              src="/logo.png"
              alt=""
              width="32"
              height="32"
              fetchPriority="high"
              decoding="async"
              className="h-7 w-7 sm:h-8 sm:w-8 object-contain shrink-0 drop-shadow-[0_0_10px_rgba(251,191,36,0.15)] group-hover:drop-shadow-[0_0_14px_rgba(251,191,36,0.35)] transition"
            />
            <span className="flex items-baseline gap-1 leading-none min-w-0">
              <span className="font-heading text-[15px] sm:text-[17px] font-bold text-[#0D1520] tracking-tight truncate">
                Amanah
              </span>
              <span
                aria-hidden="true"
                className="hidden [@media(min-width:360px)]:inline-block h-1.5 w-1.5 rounded-full bg-amber-400 self-center shrink-0"
              />
              <span className="hidden [@media(min-width:360px)]:inline font-heading text-[9.5px] sm:text-[10.5px] font-semibold uppercase tracking-wider text-[#4A5568] whitespace-nowrap">
                <span className="xl:hidden">by Zakat.Intl</span>
                <span className="hidden xl:inline">by Zakat.International</span>
              </span>
            </span>
          </button>

          {/* Primary nav — md+ (Zakat · Faraid · Will · Learn) */}
          <nav
            aria-label="Primary"
            className="hidden md:flex items-center gap-1 lg:gap-1.5"
            data-testid="site-header-nav"
          >
            <button
              data-testid="nav-zakat"
              onClick={() => navigate('/zakat/calculator')}
              className="px-2.5 py-1.5 text-sm font-semibold text-[#0D1520] hover:text-[#0D4733] hover:bg-[#F3EFEA] rounded-md transition-colors"
            >
              Zakat
            </button>
            <button
              data-testid="nav-inheritance"
              onClick={() => navigate('/inheritance')}
              className="px-2.5 py-1.5 text-sm font-semibold text-[#0D1520] hover:text-[#0D4733] hover:bg-[#F3EFEA] rounded-md transition-colors"
            >
              Faraid
            </button>
            <button
              data-testid="nav-will"
              onClick={() => navigate('/will')}
              className="px-2.5 py-1.5 text-sm font-semibold text-[#0D1520] hover:text-[#0D4733] hover:bg-[#F3EFEA] rounded-md transition-colors"
            >
              Will
            </button>
            <button
              data-testid="nav-learn"
              onClick={() => navigate('/learn')}
              className="px-2.5 py-1.5 text-sm font-semibold text-[#0D1520] hover:text-[#0D4733] hover:bg-[#F3EFEA] rounded-md transition-colors"
            >
              {t('v2.nav.learn')}
            </button>
            <DesktopMoreMenu scrollToFaq={onOpenFaq} />
          </nav>

          {/* Actions */}
          <div className="site-header__actions flex items-center gap-1.5 shrink-0">
            {/* Share — desktop only, keeps existing behavior */}
            <div className="hidden md:block">
              <HeaderShareButton testid="home-share" />
            </div>

            {/* Lite-mode badge — auto-shown only when data-lite="on" via CSS */}
            <span
              data-lite-badge
              className="items-center gap-1 h-7 px-2 rounded-full text-[10.5px] font-mono uppercase tracking-wider bg-amber-500/20 text-amber-200 border border-amber-500/30"
              title="Lite mode is active — animations and videos are disabled to save bandwidth"
            >
              Lite
            </span>

            {/* Settings chip — single trigger consolidating country · language · text-size */}
            <button
              ref={settingsTriggerRef}
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Language and text settings"
              aria-haspopup="dialog"
              aria-expanded={settingsOpen}
              data-testid="settings-trigger"
              className="inline-flex items-center gap-1.5 h-8 md:h-9 px-2 md:px-2.5 rounded-full bg-white border border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA] hover:border-[#0D4733] focus-visible:ring-2 focus-visible:ring-[#0D4733]/40 focus-visible:outline-none transition-colors"
            >
              <span aria-hidden="true" className="text-[13px] leading-none">
                {selectedLanguage?.flag || '🌐'}
              </span>
              <span className="font-mono text-[11px] font-semibold tracking-wider uppercase">
                {selectedLanguage?.code || 'EN'}
              </span>
            </button>

            {/* Mobile hamburger */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-[#0D1520] hover:bg-[#F3EFEA] focus-visible:ring-2 focus-visible:ring-[#0D4733] focus-visible:outline-none transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu-panel"
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — full navigation surface */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#FAF8F5] border-t border-[#E2DCD5] overflow-hidden shadow-[0_8px_24px_rgba(43,36,25,0.10)]"
            data-testid="mobile-menu-panel"
            id="mobile-menu-panel"
          >
            <div className="max-h-[calc(100vh-4rem)] overflow-y-auto">
              <AppMobileMenu
                onClose={() => setMobileMenuOpen(false)}
                languages={languages}
                selectedLanguage={selectedLanguage}
                updateLanguage={updateLanguage}
                countryPickerNode={<CountryPicker variant="desktop" testid="mobile-menu-country" />}
                zakatScrollHandler={() => navigate('/zakat/calculator')}
                testidPrefix="mobile-nav"
                onPageLinks={[
                  { testid: 'mobile-nav-rulings', label: 'Scholarly rulings', action: () => navigate('/learn') },
                  { testid: 'mobile-nav-faq', label: 'FAQ', action: () => { setMobileMenuOpen(false); onOpenFaq?.(); } },
                ]}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsSheet open={settingsOpen} onOpenChange={handleSettingsOpenChange} />
    </header>
  );
};

export default SiteHeader;
